/**
 * Smart Health Report — normalizer (grouped dynamic model).
 *
 * Input: GET family-members/{id}/test-results/
 *   { member, results: [...flat], grouped_results: [{ group_id, group_name, results: [...] }],
 *     ocr_results: [...flat, future], ocr_reports: [...groups, future],
 *     doctor_consultation, teleconsultation }
 *
 * Output: { member, screeningDate, screeningISO, groups, groupCount, testCount,
 *   completedCount, consultation, teleconsultation }
 *
 * Rules:
 *  - Heading = group_name from `grouped_results` (order preserved, "Other" last).
 *  - Sub-heading = item sub-group when present (sub_group / subgroup / sub_group_name).
 *  - Only COMPLETED rows are shown.
 *  - Prefers `grouped_results`; falls back to flat `results` as single "Test Results" group.
 *  - OCR-ready: `ocr_reports` (same shape as grouped_results) appended as groups,
 *    `ocr_results` (flat) appended as one "OCR Results" group. Currently empty → no-op.
 */

const normCode = (c) => String(c ?? "").trim().toLowerCase();

function toTime(v) {
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? -Infinity : t;
}

function formatDMY(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "N/A";
  return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
}

function cleanStr(v) {
  const s = String(v ?? "").trim();
  return s === "" ? null : s;
}

/** Flat row (results / ocr_results shape) → card item. */
function flatRowToItem(r) {
  return {
    parameter: r.parameter ?? r.parameter_id ?? r.parameterId ?? null,
    parameter_name: r.parameter_name ?? "—",
    parameter_code: r.parameter_code ?? "",
    value: r.value ?? null,
    unit: r.unit ?? "",
    remarks: r.remarks ?? null,
    status: r.status ?? null,
    sub_group: r.sub_group ?? r.subgroup ?? r.sub_group_name ?? r.subgroup_name ?? null,
    updated_at: r.updated_at || r.created_at || null,
  };
}

/** Grouped row (grouped_results.results / ocr_reports.results shape) → card item. */
function groupedRowToItem(r) {
  return {
    parameter: r.parameter_id ?? r.parameter ?? r.parameterId ?? null,
    parameter_name: r.parameter_name ?? "—",
    parameter_code: r.parameter_code ?? "",
    value: r.value ?? null,
    unit: r.unit ?? "",
    remarks: r.remarks ?? null,
    status: r.status ?? null,
    sub_group: r.sub_group ?? r.subgroup ?? r.sub_group_name ?? r.subgroup_name ?? null,
    updated_at: r.updated_at || r.created_at || null,
  };
}

const isCompleted = (r) => String(r?.status ?? "COMPLETED").toUpperCase() === "COMPLETED";

/**
 * Fixed report page sequence (after the Demography cover, before Doctor Consultation):
 *   Vitals → Pathology & Blood → Audiometry → Optometry ("ppto"/"optho" alias) →
 *   Dental → PFT → ECG → Pregnancy (Foetal Doppler) → anything else.
 * Matching is case-insensitive substring on group_name.
 */
const GROUP_ORDER = [
  { key: "vitals", match: ["vital"] },
  { key: "pathology", match: ["pathology", "patho", "blood", "lab"] },
  { key: "audiometry", match: ["audio"] },
  { key: "optometry", match: ["opto", "optho", "ophthal", "ppto", "vision", "eye"] },
  { key: "dental", match: ["dental", "oral", "tooth", "teeth"] },
  { key: "pft", match: ["pft", "pulmonary", "lung", "spiro"] },
  { key: "ecg", match: ["ecg"] },
  { key: "pregnancy", match: ["pregnan", "foetal", "fetal", "doppler", "obstet"] },
];

function groupRank(name) {
  const key = rankKeyOf(name);
  if (key == null) return GROUP_ORDER.length;
  return GROUP_ORDER.findIndex((e) => e.key === key);
}

function rankKeyOf(name) {
  const n = String(name ?? "").toLowerCase();
  for (const entry of GROUP_ORDER) {
    if (entry.match.some((m) => n.includes(m))) return entry.key;
  }
  return null;
}

// Page plan: Audio + Opto + Dental share one page (slot 2);
// PFT + ECG + Pregnancy share one page (slot 3);
// every other section gets its own page(s); unknown groups each get own page.
const PAGE_SLOT_OF_KEY = {
  vitals: 0,
  pathology: 1,
  audiometry: 2,
  optometry: 2,
  dental: 2,
  pft: 3,
  ecg: 3,
  pregnancy: 3,
};

/** Sort groups into the fixed sequence (stable; "Other" pinned last among unknowns). */
function sortGroupsFixed(groups) {
  return groups
    .map((g, i) => ({ g, i }))
    .sort((a, b) => {
      const ra = groupRank(a.g.group_name);
      const rb = groupRank(b.g.group_name);
      if (ra !== rb) return ra - rb;
      const ao = String(a.g.group_name || "").toLowerCase() === "other" ? 1 : 0;
      const bo = String(b.g.group_name || "").toLowerCase() === "other" ? 1 : 0;
      if (ao !== bo) return ao - bo;
      return a.i - b.i;
    })
    .map((x) => x.g);
}

/** Dedupe flat rows by parameter id → code → name, keep latest updated_at. */
function dedupeFlat(rows) {
  const map = new Map();
  rows.forEach((r) => {
    const pid = r.parameter ?? r.parameter_id ?? r.parameterId ?? null;
    const pidStr = pid !== undefined && pid !== null && String(pid).trim() !== "" ? String(pid).trim() : "";
    const key = pidStr || normCode(r.parameter_code) || String(r.parameter_name ?? "").trim().toLowerCase();
    if (!key) return;
    const prev = map.get(key);
    if (!prev) {
      map.set(key, r);
      return;
    }
    const tNew = toTime(r.updated_at ?? r.created_at);
    const tOld = toTime(prev.updated_at ?? prev.created_at);
    if (tNew > tOld || (tNew === tOld && (r.id ?? 0) > (prev.id ?? 0))) {
      map.set(key, r);
    }
  });
  return Array.from(map.values());
}

/**
 * Normalize API data → grouped dynamic report model.
 * Throws on { success: false }.
 */
export function normalizeSmartReport(apiData, dashboardMember = {}) {
  if (apiData && apiData.success === false) {
    throw new Error(apiData.message || apiData.detail || "Failed to load test results");
  }
  const apiMember = apiData?.member || {};
  const flatResults = Array.isArray(apiData?.results) ? apiData.results : [];
  const groupedRaw = Array.isArray(apiData?.grouped_results) ? apiData.grouped_results : [];

  let groups = [];

  if (groupedRaw.length > 0) {
    groups = groupedRaw.map((g) => {
      const rows = Array.isArray(g?.results) ? g.results : [];
      const items = rows.filter(isCompleted).map(groupedRowToItem);
      return {
        group_id: g?.group_id ?? null,
        group_name: cleanStr(g?.group_name) || "Other",
        items,
      };
    }).filter((g) => g.items.length > 0);
    // Fixed report sequence (Vitals → … → Foetal Doppler); unknowns incl. "Other" last.
    groups = sortGroupsFixed(groups);
  } else {
    // Fallback for backends without grouped_results: single group from flat rows.
    const completed = flatResults.filter(isCompleted);
    const items = dedupeFlat(completed).map(flatRowToItem);
    if (items.length > 0) {
      groups = [{ group_id: null, group_name: "Test Results", items }];
    }
  }

  // ---- OCR extension point (currently empty arrays → no-op) ----
  // ocr_reports uses the same { group_name, results[] } shape as grouped_results.
  const ocrGroupsRaw = Array.isArray(apiData?.ocr_reports) ? apiData.ocr_reports : [];
  ocrGroupsRaw.forEach((g) => {
    const rows = Array.isArray(g?.results) ? g.results : [];
    const items = rows.filter(isCompleted).map(groupedRowToItem);
    if (items.length > 0) {
      groups.push({
        group_id: g?.group_id ?? null,
        group_name: cleanStr(g?.group_name) || "OCR Results",
        items,
      });
    }
  });
  // ocr_results is flat → one group.
  const ocrFlat = Array.isArray(apiData?.ocr_results) ? apiData.ocr_results : [];
  const ocrCompleted = ocrFlat.filter(isCompleted);
  if (ocrCompleted.length > 0) {
    groups.push({
      group_id: null,
      group_name: "OCR Results",
      items: dedupeFlat(ocrCompleted).map(flatRowToItem),
    });
  }

  // Screening date = latest timestamp across flat + grouped items.
  const times = [
    ...flatResults.map((r) => r.updated_at || r.created_at).filter(Boolean),
    ...groups.flatMap((g) => g.items.map((i) => i.updated_at).filter(Boolean)),
  ];
  const latest = times.length ? times.sort((a, b) => toTime(b) - toTime(a))[0] : null;
  const screeningDate = latest ? formatDMY(latest) : "N/A";
  const screeningISO = latest || null;

  // Member merge (API wins; dashboard row fills fallbacks).
  const member = {
    id: apiMember.id ?? dashboardMember.id ?? null,
    member_id: apiMember.member_id ?? dashboardMember.member_id ?? "—",
    name: apiMember.name ?? dashboardMember.name ?? "—",
    relationship: apiMember.relationship ?? dashboardMember.relationship ?? null,
    photo: apiMember.photo ?? dashboardMember.photo ?? null,
    age: apiMember.age ?? dashboardMember.age ?? null,
    gender: apiMember.gender ?? dashboardMember.gender ?? null,
    phone: apiMember.phone ?? apiMember.mobile ?? dashboardMember.phone ?? dashboardMember.mobile ?? null,
    loan_number: apiMember.loan_number ?? apiMember.loanNumber ?? dashboardMember.loan_number ?? dashboardMember.loanNumber ?? null,
    aadhar_number: apiMember.aadhar_number ?? apiMember.aadhaar_number ?? apiMember.aadhar ?? dashboardMember.aadhar_number ?? dashboardMember.aadhaar_number ?? null,
  };

  const testCount = groups.reduce((n, g) => n + g.items.length, 0);
  const completedCount =
    flatResults.length > 0
      ? flatResults.filter(isCompleted).length
      : testCount;

  // ---- Doctor Consultation (from GET test-results API) ----
  const rawConsult = apiData?.doctor_consultation || null;
  const rawTele = apiData?.teleconsultation || null;
  const consultation = rawConsult
    ? {
        chiefComplaints: cleanStr(rawConsult.chief_complaints),
        durationOfSymptoms: cleanStr(rawConsult.duration_of_symptoms),
        medicalHistory: cleanStr(rawConsult.medical_history),
        clinicalFindings: cleanStr(rawConsult.clinical_findings),
        diagnosis: cleanStr(rawConsult.diagnosis),
        treatmentPlan: cleanStr(rawConsult.treatment_plan),
        followUpInstructions: cleanStr(rawConsult.follow_up_instructions),
        prescription: Array.isArray(rawConsult.prescription)
          ? rawConsult.prescription.map((p) => ({
              medicine:
                cleanStr(p?.medicine || p?.medicine_name || p?.name || p?.drug || p?.tablet) || null,
              dosage: cleanStr(p?.dosage || p?.dose) || null,
              frequency: cleanStr(p?.frequency) || null,
              duration: cleanStr(p?.duration) || null,
              instructions: cleanStr(p?.instructions || p?.notes || p?.remarks) || null,
            }))
          : [],
        createdAt: rawConsult.created_at || null,
        updatedAt: rawConsult.updated_at || null,
      }
    : null;
  const teleconsultation = rawTele
    ? {
        consultationId: cleanStr(rawTele.consultation_id) || null,
        status: cleanStr(rawTele.status) || null,
        createdAt: rawTele.created_at || null,
        startedAt: rawTele.started_at || null,
        endedAt: rawTele.ended_at || null,
      }
    : null;

  // Page slots: Audio + Opto + Dental share slot 2 (one page);
  // PFT + ECG + Pregnancy share slot 3 (one page);
  // every other known section gets its own slot; unknowns each get own page.
  let nextUnknownPage = 90;
  groups.forEach((g) => {
    const key = rankKeyOf(g.group_name);
    g.page = key != null && PAGE_SLOT_OF_KEY[key] !== undefined ? PAGE_SLOT_OF_KEY[key] : nextUnknownPage++;
  });

  return {
    member,
    screeningDate,
    screeningISO,
    groups,
    groupCount: groups.length,
    testCount,
    completedCount,
    consultation,
    teleconsultation,
    // Kept for backward compat (old renderer / cached callers). Not used anymore.
    fields: {},
    counts: { checked: testCount, normal: 0, warning: 0, outOfRange: 0 },
    additionalResults: [],
    additionalCount: 0,
  };
}
